import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { LayoutTheme } from '@shared/interfaces/layout-theme.interface';
import { setDataDarkMode, setDataSidebarCollapsed } from '@store/data.actions';
import { selectDataDarkMode, selectDataSidebarCollapsed } from '@store/data.selectors';

export interface LayoutConfig {
  preset?: string;
  primary?: string;
  surface?: string | undefined | null;
  darkTheme: boolean;
  menuMode?: string;
}

interface LayoutState {
  staticMenuDesktopInactive?: boolean;
  overlayMenuActive?: boolean;
  configSidebarVisible?: boolean;
  staticMenuMobileActive?: boolean;
  menuHoverActive?: boolean;
}

interface MenuChangeEvent {
  key: string;
  routeEvent?: boolean;
}

declare global {
  interface UpdateCallback {
    (): any;
  }

  interface Document {
    startViewTransition(callbackOptions?: UpdateCallback): ViewTransition;
  }
}

interface ViewTransition {
  ready: Promise<void>;
  finished: Promise<void>;
  skipTransition: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class LayoutService implements LayoutTheme {
  private store = inject(Store);
  private _initialized = false;

  private _config: LayoutConfig = {
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
    darkTheme: false,
    menuMode: 'static'
  };
  private _config$ = new Subject<LayoutConfig>();
  config = signal<LayoutConfig>(this._config);
  theme = computed(() => (this.config()?.darkTheme ? 'light' : 'dark'));
  primary = computed(() => this.config().primary);
  surface = computed(() => this.config().surface);

  private _state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false
  };
  state = signal<LayoutState>(this._state);
  isDarkTheme = computed(() => this.config().darkTheme);
  isOverlay = computed(() => this.config().menuMode === 'overlay');
  isSidebarActive = computed(() => this.state().overlayMenuActive || this.state().staticMenuMobileActive);
  isSidebarOpen = computed(() => {
    const state = this.state();
    if (this.isOverlay()) {
      return !!state.overlayMenuActive;
    }
    return this.isDesktop() ? !state.staticMenuDesktopInactive : !!state.staticMenuMobileActive;
  });

  private _overlay$ = new Subject<any>();
  overlay$ = this._overlay$.asObservable();

  private _menu$ = new Subject<MenuChangeEvent>();
  menu$ = this._menu$.asObservable();

  private _reset$ = new Subject();
  reset$ = this._reset$.asObservable();

  transition = signal<boolean>(false);

  constructor() {
    this.store.select(selectDataDarkMode).subscribe(darkMode => {
      this._config.darkTheme = darkMode;
    });
    this.store.select(selectDataSidebarCollapsed).subscribe(sidebarCollapsed => {
      this.state.update((prev) => ({ ...prev, staticMenuDesktopInactive: sidebarCollapsed }));
    });
    effect(() => {
      const config = this.config();
      if (config) {
        this.onConfigUpdate();
        this.applyDarkModeTransition(config);
      }
    });
  }

  private applyDarkModeTransition(config: LayoutConfig): void {
    if ('startViewTransition' in document) {
      this.executeViewTransition(config);
    } else {
      this.toggleDarkModeClasses(config);
      this.markTransitionComplete();
    }
  }

  private executeViewTransition(config: LayoutConfig): void {
    const transition = document.startViewTransition!(() => this.toggleDarkModeClasses(config));
    transition.ready
      .then(() => this.markTransitionComplete())
      .catch(() => {
        this.toggleDarkModeClasses(config);
        this.markTransitionComplete();
      });
  }

  private toggleDarkModeClasses(config: LayoutConfig): void {
    const { darkTheme } = config;
    const classList = document.documentElement.classList;
    darkTheme
      ? classList.add('app-dark')
      : classList.remove('app-dark');
  }

  private markTransitionComplete(): void {
    this.transition.set(true);
    // Auto-reset after animation frame
    requestAnimationFrame(() => this.transition.set(false));
  }

  toggleDarkMode(): void {
    this.config.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.state.update((prev) => ({ ...prev, overlayMenuActive: !this.state().overlayMenuActive }));
      if (this.state().overlayMenuActive) {
        this._overlay$.next(null);
      }
    }
    if (this.isDesktop()) {
      const staticMenuDesktopInactive = !this.state().staticMenuDesktopInactive;
      this.state.update((prev) => ({ ...prev, staticMenuDesktopInactive }));
      this.store.dispatch(setDataSidebarCollapsed({ sidebarCollapsed: staticMenuDesktopInactive }));
    } else {
      this.state.update((prev) => ({ ...prev, staticMenuMobileActive: !this.state().staticMenuMobileActive }));
      if (this.state().staticMenuMobileActive) {
        this._overlay$.next(null);
      }
    }
  }

  isDesktop() {
    return window.innerWidth > 1199;
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this._config = { ...this.config() };
    this.store.dispatch(setDataDarkMode({ darkMode: this.config().darkTheme }))
    this._config$.next(this.config());
  }

  onMenuStateChange(event: MenuChangeEvent) {
    this._menu$.next(event);
  }

  reset() {
    this._reset$.next(true);
  }
}
